import { useMemo, useContext, useState } from 'react';
import { createColumnHelper, ColumnDef } from '@tanstack/react-table';
import { AdminOrganizations } from '@platform/client';
import { AdminOrganization, OrganizationApplicationStatusEnum } from '@platform/client/schemas';
import { keepPreviousData } from '@tanstack/react-query';
import { Icon, PillBadge } from '@platform/components';
import { getDaysSince } from '../../../util/DateHelpers';
import { AdminTable, AdminTableContext } from '../../AdminTable';
import { AdminActionsContainer, AdminListPageWrapper } from '../../AdminListPage';
import { UpdateAppStatusModal } from './UpdateApplicationStatusModal';
import { ProjectLink, OrganizationProfileLink } from '../../components/ProfileLinks';

export const OrganizationsAdmin = () => {
  return (
    <AdminListPageWrapper title={'Organizations'}>
      <OrganizationsAdminContent />
    </AdminListPageWrapper>
  );
};

const columnHelper = createColumnHelper<AdminOrganization>();

export const OrganizationsAdminContent = () => {
  const { adminQueryOptions } = useContext(AdminTableContext);
  const [updateAppStatusOrg, setUpdateAppStatusOrg] = useState<AdminOrganization>();

  const openAppStatusModal = (row: AdminOrganization) => {
    setUpdateAppStatusOrg(row);
  };

  const closeVettingModal = () => {
    setUpdateAppStatusOrg(undefined);
  };

  const { data, isSuccess, isLoading, isError } = AdminOrganizations.useGetOrganizations(
    adminQueryOptions,
    {
      query: {
        placeholderData: keepPreviousData,
      },
    },
  );
  const { organizations = [], filteredCount = 0 } = data || {};
  const columns = useMemo(
    () =>
      [
        columnHelper.accessor('id', {
          header: 'Id',
          cell: (id) => id.getValue(),
          meta: {
            filterVariant: 'text',
          },
          size: 25,
        }),
        columnHelper.accessor('name', {
          header: 'Name',
          cell: ({ row }) => {
            return (
              <OrganizationProfileLink
                organizationId={row.original.id}
                organizationName={row.original.name}
              />
            );
          },
          meta: {
            filterVariant: 'text',
          },
          size: 300,
        }),
        columnHelper.accessor('projects', {
          header: 'Projects',
          cell: (projects) => {
            // sorted to avoid projects re-ordering based on api
            // I believe refactoring the API will fix this when we no longer joinedLoad
            const projectsUnsorted = projects.getValue();
            const sortedProjects = projectsUnsorted?.sort((a, b) => {
              return b.id - a.id;
            });
            return (
              <div className='flex flex-col text-wrap'>
                {sortedProjects?.map((project) => {
                  return (
                    <ProjectLink
                      key={project.id}
                      projectId={project.id}
                      projectName={`${project.name.slice(0, 41)} ... [${project.id}]`}
                    />
                  );
                })}
              </div>
            );
          },
          enableColumnFilter: false,
          enableSorting: false,
          size: 250,
        }),
        columnHelper.accessor('matchingNetworks', {
          header: 'Matching Networks',
          cell: (matchingNetworks) => {
            const sortedNetworks = matchingNetworks.getValue()?.sort((a, b) => a.localeCompare(b));
            return sortedNetworks?.map((network) => {
              return (
                <PillBadge variant='plain' color='indigo' key={network} size='sm'>
                  {network}
                </PillBadge>
              );
            });
          },
          meta: {
            filterVariant: 'text',
          },
          size: 25,
        }),
        columnHelper.accessor('applicationStatus', {
          header: 'Application Status',
          cell: ({ row }) => {
            return row.original.applicationStatus ?
                <div className='flex justify-between'>
                  <ApplicationStageBadge
                    status={row.original.applicationStatus}
                    statusUpdated={row.original.applicationUpdated ?? ''}
                  />
                  <button onClick={() => openAppStatusModal(row.original)}>
                    <Icon type='edit-01' size='14' />
                  </button>
                </div>
              : <div className='text-gray-500'>N/A</div>;
          },
          enableSorting: false,
          meta: {
            filterVariant: 'text',
          },
          size: 25,
        }),
        columnHelper.accessor('createdAt', {
          header: 'Created At',
          cell: (createdAt) => new Date(createdAt.getValue()).toLocaleString(),
          size: 25,
        }),
        columnHelper.display({
          id: 'actions',
          cell: ({ row }) => {
            return (
              <AdminActionsContainer
                actions={[
                  { text: 'Review', type: 'link', href: `/review/${row.original.id}` },
                  {
                    text: 'Edit',
                    type: 'link',
                    href: `${import.meta.env['VITE_LEGACY_APP_URL']}/admin/organization/${row.original.id}/edit`,
                  },
                ]}
              />
            );
          },
          header: () => <span className='overflow-ellipsis whitespace-nowrap'>Actions</span>,
          size: 25,
        }),
      ] as ColumnDef<AdminOrganization>[],
    [],
  );

  return (
    <>
      <AdminTable
        data={organizations}
        isSuccess={isSuccess}
        isLoading={isLoading}
        isError={isError}
        filteredCount={filteredCount}
        columns={columns}
        enableFiltering={true}
      />
      {updateAppStatusOrg && (
        <UpdateAppStatusModal
          onClose={closeVettingModal}
          organizationId={updateAppStatusOrg.id}
          status={updateAppStatusOrg.applicationStatus as OrganizationApplicationStatusEnum}
          applicationId={updateAppStatusOrg.applicationId as number}
        />
      )}
    </>
  );
};

export const getOrgAppStatusBadgeColor = (status: OrganizationApplicationStatusEnum) => {
  switch (status) {
    case OrganizationApplicationStatusEnum.approved:
      return 'success-500';
    case OrganizationApplicationStatusEnum.approved_match:
      return 'success-100';
    case OrganizationApplicationStatusEnum.rejected:
      return 'error';
    case OrganizationApplicationStatusEnum.info_requested:
      return 'orange';
    case OrganizationApplicationStatusEnum.pending:
      return 'warning';
    default:
      throw new Error(`Unknown application status: ${status}`);
  }
};

const ApplicationStageBadge = (props: {
  status: OrganizationApplicationStatusEnum;
  statusUpdated: string;
  additionalClassnames?: string;
}) => {
  const { status, statusUpdated } = props;
  const badgeColor = getOrgAppStatusBadgeColor(status);
  const daysSinceApplicationUpdated = getDaysSince(statusUpdated);
  const vettedDateText =
    daysSinceApplicationUpdated === 0 ? 'today' : (
      `${daysSinceApplicationUpdated} day${daysSinceApplicationUpdated > 1 ? 's' : ''} ago`
    );
  return (
    <PillBadge
      variant='plain'
      color={badgeColor}
      size='sm'
      additionalClassnames={props.additionalClassnames}
    >
      {status.split('_').join(' ').toUpperCase()}
      {/* do you need to highlight passed 7 days */}
      {(status === OrganizationApplicationStatusEnum.pending ||
        status === OrganizationApplicationStatusEnum.info_requested) && (
        <span className='text-xs text-gray-500 ml-1'>({vettedDateText})</span>
      )}
    </PillBadge>
  );
};
